import { topologicalSort } from '../lib/workflowGraph';
import type { ProposedAction, ProposedActionVisibility } from './types';

/**
 * Why a prerequisite has not reached `run_status = Succeeded`. `rejected`,
 * `failed`, `closed` and `missing` never will, so the dependent can only be
 * rejected or re-planned.
 */
export type UnmetDependencyState =
  | 'pending'
  | 'approved'
  | 'running'
  | 'failed'
  | 'rejected'
  | 'closed'
  | 'missing';

export interface UnmetDependency {
  externalId: string;
  action: ProposedAction | null;
  state: UnmetDependencyState;
}

export interface EnrichedActiveProposedAction {
  action: ProposedAction;
  visibility: ProposedActionVisibility;
  unmetDependencies: UnmetDependency[];
}

function hasSucceeded(action: ProposedAction | undefined): boolean {
  return action?.run_status === 'Succeeded';
}

function unmetDependencyState(dependency: ProposedAction | undefined): UnmetDependencyState {
  if (dependency === undefined) {
    return 'missing';
  }
  if (dependency.decision === 'Rejected') {
    return 'rejected';
  }
  if (dependency.run_status === 'Failed') {
    return 'failed';
  }
  if (dependency.run_status === 'Running') {
    return 'running';
  }
  if (dependency.todo_status !== 'Open') {
    return 'closed';
  }
  if (dependency.decision === 'Pending') {
    return 'pending';
  }
  return 'approved';
}

export function describeUnmetDependencies(
  action: ProposedAction,
  allActions: readonly ProposedAction[]
): UnmetDependency[] {
  const actionByExternalId = new Map<string, ProposedAction>();
  for (const candidate of allActions) {
    actionByExternalId.set(candidate.external_id, candidate);
  }

  const unmet: UnmetDependency[] = [];
  for (const dependencyExternalId of action.depends_on_action_external_ids) {
    const dependency = actionByExternalId.get(dependencyExternalId);
    if (!hasSucceeded(dependency)) {
      unmet.push({
        externalId: dependencyExternalId,
        action: dependency ?? null,
        state: unmetDependencyState(dependency),
      });
    }
  }

  return unmet;
}

export function deriveUnmetDependencies(
  action: ProposedAction,
  allActions: readonly ProposedAction[]
): string[] {
  return describeUnmetDependencies(action, allActions).map(({ externalId }) => externalId);
}

export function deriveVisibility(
  action: ProposedAction,
  allActions: readonly ProposedAction[]
): ProposedActionVisibility {
  if (action.todo_status !== 'Open') {
    return 'closed';
  }

  switch (action.run_status) {
    case 'Running':
      return 'running';
    case 'Succeeded':
      return 'succeeded';
    case 'Failed':
      return 'failed';
    case 'Idle':
    case undefined:
      break;
    default:
      break;
  }

  switch (action.decision) {
    case 'Pending':
      return 'pending';
    case 'Rejected':
      return 'closed';
    case 'Approved': {
      const unmetDependencyExternalIds = deriveUnmetDependencies(action, allActions);
      return unmetDependencyExternalIds.length === 0 ? 'executable' : 'blocked';
    }
    default:
      return 'closed';
  }
}

export function selectActiveActions(actions: readonly ProposedAction[]): ProposedAction[] {
  const openActions = actions.filter(
    (action) => action.todo_status === 'Open' && action.decision !== 'Rejected'
  );
  return topologicalSort(openActions);
}

export function selectEnrichedActiveActions(
  allActions: readonly ProposedAction[]
): EnrichedActiveProposedAction[] {
  const activeActions = selectActiveActions(allActions);
  return activeActions.map((action) => ({
    action,
    visibility: deriveVisibility(action, allActions),
    unmetDependencies: describeUnmetDependencies(action, allActions),
  }));
}
